import api from './api';
import type { LoginRequest, LoginResponse, User } from '../types';
import { useAuthStore } from '../store/auth.store';

export const authService = {
  async login(data: LoginRequest): Promise<LoginResponse> {
    const response = await api.post<LoginResponse>('/auth/login', data);
    const { accessToken, refreshToken, user } = response.data;

    // Store tokens in localStorage and zustand
    localStorage.setItem('access_token', accessToken);
    localStorage.setItem('refresh_token', refreshToken);

    const store = useAuthStore.getState();
    store.setTokens(accessToken, refreshToken);
    store.setUser(user);

    return response.data;
  },

  async logout(): Promise<void> {
    try {
      await api.post('/auth/logout');
    } finally {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      useAuthStore.getState().logout();
    }
  },

  async getCurrentUser(): Promise<User> {
    const response = await api.get<User>('/auth/me');
    return response.data;
  },

  async refreshToken(): Promise<string> {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) throw new Error('No refresh token');

    const response = await api.post<{ access_token: string; refresh_token: string }>(
      '/auth/refresh',
      { refresh_token: refreshToken },
    );

    const { access_token, refresh_token: newRefreshToken } = response.data;

    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', newRefreshToken);

    const store = useAuthStore.getState();
    store.setTokens(access_token, newRefreshToken);

    return access_token;
  },

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await api.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
  },

  isAuthenticated(): boolean {
    return !!localStorage.getItem('access_token');
  },
};
